import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { NgOtpInputComponent, NgOtpInputModule } from 'ng-otp-input';
import { GlobalFunctions } from '../../../core/global-functions';
import { AuthService } from '../../../core/auth/auth.service';
import { MessageService } from 'primeng/api';
import { LocalStorageHelper } from '../../../core/helpers/local-storage.helper';
import { CONSTANTS } from '../../../core/constants';
import { CommonService } from '../../../core/common.service';

@Component({
  selector: 'app-otp',
  standalone: true,
  imports: [CommonModule, NgOtpInputModule, FormsModule, ReactiveFormsModule, RouterModule],
  providers: [GlobalFunctions],
  templateUrl: './otp.component.html',
  styleUrl: './otp.component.scss',
})
export class OtpComponent implements OnInit, OnDestroy {
  otpCode: string = '';
  userEmail: string = '';
  otpPurpose: string = 'verification';
  isBtnLoading: boolean = false;
  isResending: boolean = false;
  resendTimer: number = 0;
  timerInterval: any;

  @ViewChild(NgOtpInputComponent, { static: false })
  ngOtpInput!: NgOtpInputComponent;

  constructor(
    private _router: Router,
    private _globalFunctions: GlobalFunctions,
    private _authServices: AuthService,
    private messageService: MessageService,
    private localStorage: LocalStorageHelper,
    private _commonService: CommonService,
  ) {}

  ngOnInit(): void {
    const email = this.localStorage.getItem('userEmail');
    if (!email) {
      this._router.navigate(['/login']);
      return;
    }
    this.userEmail = email;
    this.otpPurpose = this.localStorage.getItem('otpPurpose') || 'verification';
    this.startResendTimer();
  }

  ngOnDestroy(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  startResendTimer(): void {
    this.resendTimer = 30;
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.timerInterval = setInterval(() => {
      if (this.resendTimer > 0) {
        this.resendTimer--;
      } else {
        clearInterval(this.timerInterval);
      }
    }, 1000);
  }

  onOtpChange(event: any): void {
    this.otpCode = event || '';
  }

  changeEmail(): void {
    this.localStorage.removeItem('userEmail');
    this.localStorage.removeItem('otpPurpose');
    if (this.otpPurpose === 'forgot_password') {
      this._router.navigate(['/forget-password']);
    } else {
      this._router.navigate(['/register']);
    }
  }

  submitOTP(): void {
    if (!this.otpCode || this.otpCode.length < 6) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please enter a valid 6-digit OTP code'
      });
      return;
    }

    const userObj: any = {
      channel: 'email',
      identifier: this.userEmail,
      purpose: this.otpPurpose,
      otp: this.otpCode,
    };

    this.isBtnLoading = true;
    this._authServices.verifyOtp(userObj).subscribe({
      next: (result: any) => {
        if (result && result?.success) {
          if (this.otpPurpose === 'forgot_password') {
            if (result.data?.reset_token) {
              this.localStorage.setItem('resetToken', result.data.reset_token);
            }
            this.localStorage.removeItem('userEmail');
            this.localStorage.removeItem('otpPurpose');
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: result.message || result.Message || 'OTP verified successfully'
            });
            this._router.navigate(['/reset-password']);
          } else {
            this.localStorage.removeItem('userEmail');
            this.localStorage.removeItem('otpPurpose');
            this._authServices.setRole(result.data.role);
            const isStudent = result.data?.role === 'student';
            this.getProfile(isStudent, result);
          }
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: result?.message || result?.Message || 'Invalid OTP code'
          });
          this.isBtnLoading = false;
        }
      },
      error: (error: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.message || error.error?.Message || 'Verification failed'
        });
        this.isBtnLoading = false;
      },
    });
  }

  getProfile(isStudent: boolean = false, loginUserResponse: any) {
    this.isBtnLoading = true;
    this._commonService.getProfile(isStudent).subscribe({
      next: (result: any) => {
        if (result?.success) {
          this._commonService.setProfileData(result.data);
          this.messageService.add({ severity: 'success', summary: 'Success', detail: loginUserResponse.message });
          this.redirectAfterLogin(isStudent, loginUserResponse.data);
        } else {
          this._globalFunctions.successErrorHandling(result, this, true);
          this.isBtnLoading = false;
        }
      },
      error: (error: any) => {
        this._globalFunctions.errorHanding(error, this, true);
        this.isBtnLoading = false;
      },
    });
  }

  redirectAfterLogin(isStudent: boolean = false, userData: any) {
    if (isStudent) {
      this._router.navigate(['/find-tutors']);
      this.isBtnLoading = false;
    } else {
      const status = userData.profile_status;
      if (status === 'submitted') {
        this._router.navigate(['/thank-you']);
        this.isBtnLoading = false;
      } else if (status === 'approved') {
        this._router.navigate(['/tutor/dashboard']);
        this.isBtnLoading = false;
      } else if (status === 'pending' || status === 'rejected') {
        this._commonService.getProfileStatus().subscribe({
          next: (result: any) => {
            if (result && result.success && result.data) {
              const steps = result.data;
              const firstIncompleteStep = steps.find(
                (step: any) =>
                  step.status !== 'completed' && step.status !== 'approved',
              );
              if (firstIncompleteStep) {
                this.redirectToStep(firstIncompleteStep.step_key);
              } else {
                this._router.navigate([
                  '/tutor-signup/' + CONSTANTS.tutorSubmitUrl,
                ]);
              }
            } else {
              this._router.navigate([
                '/tutor-signup/' + CONSTANTS.tutorAboutUrl,
              ]);
            }
            this.isBtnLoading = false;
          },
          error: (error: any) => {
            this._router.navigate(['/tutor-signup/' + CONSTANTS.tutorAboutUrl]);
            this.isBtnLoading = false;
          },
        });
      } else {
        this._router.navigate(['/tutor-signup/' + CONSTANTS.tutorAboutUrl]);
        this.isBtnLoading = false;
      }
    }
  }

  redirectToStep(stepKey: string) {
    switch (stepKey) {
      case 'about':
        this._router.navigate(['/tutor-signup/' + CONSTANTS.tutorAboutUrl]);
        break;
      case 'subjects':
        this._router.navigate(['/tutor-signup/' + CONSTANTS.tutorSubjectsUrl]);
        break;
      case 'languages':
        this._router.navigate(['/tutor-signup/' + CONSTANTS.tutorLanguagesUrl]);
        break;
      case 'education':
        this._router.navigate(['/tutor-signup/' + CONSTANTS.tutorEducationUrl]);
        break;
      case 'experience':
        this._router.navigate([
          '/tutor-signup/' + CONSTANTS.tutorExperienceUrl,
        ]);
        break;
      case 'availability':
        this._router.navigate([
          '/tutor-signup/' + CONSTANTS.tutorAvailibilityUrl,
        ]);
        break;
      case 'rate':
        this._router.navigate([
          '/tutor-signup/' + CONSTANTS.tutorRatPerHourUrl,
        ]);
        break;
      case 'submit':
        this._router.navigate(['/tutor-signup/' + CONSTANTS.tutorSubmitUrl]);
        break;
      default:
        this._router.navigate(['/tutor-signup/' + CONSTANTS.tutorAboutUrl]);
        break;
    }
  }

  resendOTP() {
    if (this.resendTimer > 0 || this.isResending) return;

    this.isResending = true;
    const resendObservable = this.otpPurpose === 'forgot_password'
      ? this._authServices.forgetPassword(this.userEmail)
      : this._authServices.resendOtp({ email: this.userEmail });

    resendObservable.subscribe({
      next: (result: any) => {
        this.isResending = false;
        if (result && result.success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: result.message || result.Message || 'OTP code resent successfully'
          });
          this.startResendTimer();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: result?.message || result?.Message || 'Failed to resend OTP'
          });
        }
      },
      error: (error: any) => {
        this.isResending = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.message || error.error?.Message || 'Failed to resend OTP'
        });
      }
    });
  }
}
